import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, Paper, Radio, RadioGroup, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { PosService } from '../../services/pos.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PosSale, PosSaleStatus, PosPostingStatus, PosSettings } from '../../interfaces/pos.interface';
import { formatMoney } from '../../utils/currency';
import { formatDateTime } from '../../utils/dateUtils';
import { printPosReceipt, PosReceiptFormat } from './PosReceipt';

const SALE_STATUS_COLORS: Record<PosSaleStatus, 'success' | 'default'> = {
    COMPLETED: 'success',
    VOIDED: 'default',
};

// See PosSaleList.tsx's identical map and its comment: REVERSAL_FAILED is a "needs attention"
// state, never an "error" state -- the sale itself voided successfully.
const POSTING_STATUS_COLORS: Record<PosPostingStatus, 'success' | 'default' | 'warning' | 'error'> = {
    PENDING: 'warning',
    POSTED: 'success',
    FAILED: 'error',
    SKIPPED: 'default',
    REVERSAL_FAILED: 'warning',
};

// The void's own authority per spec §10's role table -- the mirror image of PosSaleQueryController's
// postNow role list. Accountant can SEE a sale (the query controller admits it) but must never be
// offered the void button: the void endpoint itself 403s an Accountant.
const VOID_ROLES = ['InstitutionAdmin', 'StoreAdmin', 'StoreManager'];

// '' and whitespace-only are not real values -- a manager who typed then deleted a reason, or the
// backend echoing a blank string it stored for some other reason, must render the same as never
// having a reason at all (review F2's display half: `?? '—'` alone does not catch '').
const displayOrDash = (value: string | null | undefined): string => (
    value && value.trim() ? value : '—'
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ minWidth: 140 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography>{children}</Typography>
    </Box>
);

const PosSaleDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const toast = useToast();

    const [sale, setSale] = useState<PosSale | null>(null);
    const [loading, setLoading] = useState(true);
    // Distinct from `sale` staying null merely because nothing has loaded yet: this is only set
    // when `getSale` itself rejects (review F3). Without it, a bad id or a transient failure left
    // the screen spinning forever with no error state and no way back -- Task 7 links into this
    // route from the unposted queue, so a stale link would strand the manager here.
    const [loadError, setLoadError] = useState(false);

    const [voidOpen, setVoidOpen] = useState(false);
    const [voidReason, setVoidReason] = useState('');
    const [voiding, setVoiding] = useState(false);

    const [reprintOpen, setReprintOpen] = useState(false);
    const [reprintFormat, setReprintFormat] = useState<PosReceiptFormat>('MM80');
    const [reprinting, setReprinting] = useState(false);

    const userRoles: string[] = user && user.roles
        ? (Array.isArray(user.roles) ? user.roles : [user.roles])
        : [];
    const canVoid = VOID_ROLES.some(role => userRoles.includes(role));

    useEffect(() => {
        if (id) load(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const load = async (saleId: string) => {
        try {
            setLoading(true);
            setLoadError(false);
            const response = await PosService.getSale(saleId);
            setSale(response);
        } catch (err) {
            setLoadError(true);
            toast.showApiError(err, 'Failed to load sale');
        } finally {
            setLoading(false);
        }
    };

    const openReprintDialog = () => {
        setReprintFormat('MM80');
        setReprintOpen(true);
    };
    const closeReprintDialog = () => setReprintOpen(false);

    const handleReprint = async () => {
        if (!sale) return;
        try {
            setReprinting(true);
            // Per ruling R1: no backend endpoint for reprint. The sale detail already carries
            // lines and tenders (this screen's own load); the header/footer strings live on
            // settings, fetched fresh here rather than cached, so a header/footer edit takes
            // effect on the very next reprint without requiring this screen to be reloaded.
            const settings: PosSettings = await PosService.getSettings();
            printPosReceipt(sale, settings, reprintFormat, {
                storeName: sale.storeName,
                terminalLabel: sale.terminalCode,
                cashierName: sale.cashierName,
            });
            closeReprintDialog();
        } catch (err) {
            toast.showApiError(err, 'Failed to load receipt settings');
        } finally {
            setReprinting(false);
        }
    };

    const openVoidDialog = () => {
        setVoidReason('');
        setVoidOpen(true);
    };
    const closeVoidDialog = () => {
        setVoidOpen(false);
        setVoidReason('');
    };

    const handleConfirmVoid = async () => {
        if (!id) return;
        try {
            setVoiding(true);
            // A blank reason must reach the backend as absent, not as '' -- PosService.voidSale
            // already treats a falsy reason as omitted (review F2), but trimming here too means a
            // whitespace-only reason is treated the same as a genuinely blank one.
            const trimmedReason = voidReason.trim();
            const response = await PosService.voidSale(id, trimmedReason || undefined);
            setSale(response);
            closeVoidDialog();
            // A void that lands with postingStatus REVERSAL_FAILED is NOT a failed void: stock
            // came back and the sale IS voided. Only the journal-entry reversal failed, and it is
            // retryable from the unposted queue -- telling the manager "void failed" here would be
            // false and would invite a second, unnecessary void attempt. The explanation itself is
            // rendered from `sale.postingStatus` below (the Void details panel), the same single
            // source of truth used whether this screen just performed the void or is simply
            // displaying a sale someone else voided earlier -- so there is exactly one place this
            // sentence is written, not a transient copy that would go stale on reload (review F1
            // proved this holds on a fresh load, not just the just-voided path).
            toast.showSuccess('Sale voided');
        } catch (err) {
            toast.showApiError(err, 'Failed to void sale');
        } finally {
            setVoiding(false);
        }
    };

    if (loadError) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={() => id && load(id)}>
                            Retry
                        </Button>
                    )}
                >
                    Failed to load this sale.
                </Alert>
                <Button component={RouterLink} to="/pos/sales" sx={{ mt: 2 }}>
                    Back to Sales
                </Button>
            </Box>
        );
    }

    if (loading || !sale) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h5">{sale.receiptNo}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sold {formatDateTime(sale.soldAt)}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip color={SALE_STATUS_COLORS[sale.status]} label={sale.status} />
                    <Chip color={POSTING_STATUS_COLORS[sale.postingStatus]} label={sale.postingStatus} />
                    {/* Reprintable regardless of status -- a voided sale is still reprintable,
                        it just carries the VOIDED banner (PosReceipt), which is the whole point:
                        a void must never disappear on reprint. */}
                    <Button variant="outlined" onClick={openReprintDialog}>
                        Reprint Receipt
                    </Button>
                    {sale.status !== 'VOIDED' && canVoid && (
                        <Button color="error" variant="outlined" onClick={openVoidDialog}>
                            Void Sale
                        </Button>
                    )}
                </Box>
            </Box>

            {sale.stockWentNegative && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This sale was rung with insufficient stock on hand.
                </Alert>
            )}

            {sale.status === 'VOIDED' && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Void details</Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Field label="Voided by">{displayOrDash(sale.voidedByName ?? sale.voidedBy)}</Field>
                        <Field label="Voided at">{sale.voidedAt ? formatDateTime(sale.voidedAt) : '—'}</Field>
                        <Field label="Reason">{displayOrDash(sale.voidedReason)}</Field>
                    </Box>
                    {sale.postingStatus === 'REVERSAL_FAILED' && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            The sale was voided and stock returned. The ledger reversal did not
                            complete{sale.postingError ? `: ${sale.postingError}` : ''}. This is
                            NOT a failed void -- use "Post now" on the unposted sales queue to
                            retry the reversal.
                        </Alert>
                    )}
                </Paper>
            )}

            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Sale details</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Field label="Store">{sale.storeName ?? sale.storeId}</Field>
                    <Field label="Terminal">{sale.terminalCode ?? sale.terminalId}</Field>
                    <Field label="Cashier">{sale.cashierName ?? sale.cashierUserId}</Field>
                    <Field label="App Version">{sale.appVersion ?? '—'}</Field>
                </Box>
            </Paper>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ p: 2, pb: 0 }}>Lines</Typography>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Discount</TableCell>
                            <TableCell align="right">Tax</TableCell>
                            <TableCell align="right">Line Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(sale.lines ?? []).map(line => (
                            <TableRow key={line.id}>
                                <TableCell>{line.itemName}</TableCell>
                                <TableCell>{line.itemCode}</TableCell>
                                {/* qty is BIGINT end to end -- never fractional -- so this renders
                                    the plain integer, not toFixed(2)/formatMoney. */}
                                <TableCell align="right">{line.qty}</TableCell>
                                <TableCell align="right">{formatMoney(line.unitPrice)}</TableCell>
                                <TableCell align="right">{formatMoney(line.lineDiscountAmount)}</TableCell>
                                <TableCell align="right">{formatMoney(line.taxAmount)}</TableCell>
                                <TableCell align="right">{formatMoney(line.lineTotal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ p: 2, pb: 0 }}>Tenders</Typography>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Method</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Tendered</TableCell>
                            <TableCell align="right">Change</TableCell>
                            <TableCell>Reference</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(sale.tenders ?? []).map(tender => (
                            <TableRow key={tender.id}>
                                <TableCell>{tender.method}</TableCell>
                                <TableCell align="right">{formatMoney(tender.amount)}</TableCell>
                                <TableCell align="right">{formatMoney(tender.tenderedAmount)}</TableCell>
                                <TableCell align="right">{formatMoney(tender.changeAmount)}</TableCell>
                                <TableCell>{tender.reference ?? '—'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Totals</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Field label="Subtotal">{formatMoney(sale.subtotalAmount)}</Field>
                    <Field label="Discount">{formatMoney(sale.discountAmount)}</Field>
                    <Field label="Tax">{formatMoney(sale.taxAmount)}</Field>
                    <Field label="Rounding">{formatMoney(sale.roundingAmount)}</Field>
                    <Box sx={{ minWidth: 140 }}>
                        <Typography variant="body2" color="text.secondary">Total</Typography>
                        <Typography variant="h6">{formatMoney(sale.totalAmount)}</Typography>
                    </Box>
                    <Field label="Tendered">{formatMoney(sale.tenderTotal)}</Field>
                    <Field label="Change">{formatMoney(sale.changeAmount)}</Field>
                </Box>
            </Paper>

            <Dialog open={reprintOpen} onClose={closeReprintDialog} fullWidth maxWidth="xs">
                <DialogTitle>Reprint Receipt</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Alert severity="info">
                        This reprint is clearly marked as a reprint and cannot be mistaken for the
                        original receipt.
                    </Alert>
                    <RadioGroup
                        value={reprintFormat}
                        onChange={e => setReprintFormat(e.target.value as PosReceiptFormat)}
                    >
                        <FormControlLabel value="MM80" control={<Radio />} label="80mm Thermal (counter roll)" />
                        <FormControlLabel value="A4" control={<Radio />} label="A4 (office printer)" />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeReprintDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleReprint} disabled={reprinting}>
                        Print
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={voidOpen} onClose={closeVoidDialog} fullWidth maxWidth="xs">
                <DialogTitle>Void Sale?</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Alert severity="warning">
                        Voiding this sale returns its stock and reverses the ledger entry. This
                        cannot be undone.
                    </Alert>
                    <TextField
                        label="Reason (optional)"
                        value={voidReason}
                        onChange={e => setVoidReason(e.target.value)}
                        multiline
                        minRows={2}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeVoidDialog}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleConfirmVoid} disabled={voiding}>
                        Confirm Void
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PosSaleDetail;
